import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { IconChevronRight, IconPlug } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import {
  describeChange,
  MetricPanel,
  MetricStripSkeleton,
  type Metric,
} from "@/components/dashboard/metric-cell";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import { programService } from "@/services/program.service";
import type { ProgramAnalyticsRow } from "@/types/programs-analytics";
import { formatAmount, formatNumber } from "@/utils/formatters";

const money = (value: number) => (
  <span className="tabular-nums font-medium text-foreground">{formatAmount(value)}</span>
);
const count = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

function rateText(type: string, value: number) {
  return type === "FIXED" ? `${formatAmount(value)} per order` : `${formatNumber(value)}%`;
}

function renewalDeal(row: ProgramAnalyticsRow) {
  const s = row.renewalSettings;
  if (!s.enabled) return "Off";
  const rate = s.rateType === "FIXED" ? `${formatAmount(s.rate)} per renewal` : `${formatNumber(s.rate)}%`;
  return s.paidCap === null ? rate : `${rate}, first ${formatNumber(s.paidCap)}`;
}

const programColumns = (withRecurring: boolean): ColumnDef<ProgramAnalyticsRow>[] => [
  {
    accessorKey: "name",
    header: "Program",
    cell: ({ row }) => (
      <span className="flex min-w-0 flex-col">
        <span className="block max-w-[16rem] truncate font-medium text-foreground" title={row.original.name}>
          {row.original.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {rateText(row.original.commissionType, row.original.commissionValue)}
        </span>
      </span>
    ),
  },
  {
    accessorKey: "affiliates",
    header: "Affiliates",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="flex flex-col">
        {count(row.original.affiliates)}
        {row.original.period.sellers > 0 && (
          <span className="text-xs text-muted-foreground">
            {formatNumber(row.original.period.sellers)} selling
          </span>
        )}
      </span>
    ),
  },
  { id: "orders", header: "Orders", meta: { fit: true }, cell: ({ row }) => count(row.original.period.orders) },
  { id: "revenue", header: "Revenue", meta: { fit: true }, cell: ({ row }) => money(row.original.period.revenue) },
  {
    id: "commission",
    header: "Commission",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original.period.commission)}</span>,
  },
  ...(withRecurring
    ? ([
        {
          id: "renewalDeal",
          header: "Renewal rate",
          meta: { fit: true },
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-muted-foreground">{renewalDeal(row.original)}</span>
          ),
        },
        {
          id: "subscriptions",
          header: "Subscriptions",
          meta: { fit: true },
          cell: ({ row }) => {
            const r = row.original.renewals;
            if (!r || r.subscriptions === 0) return <span className="text-muted-foreground">None yet</span>;
            return (
              <span className="flex flex-col">
                {count(r.active)}
                {r.cancelled > 0 && (
                  <span className="text-xs text-muted-foreground">{formatNumber(r.cancelled)} cancelled</span>
                )}
              </span>
            );
          },
        },
        {
          id: "renewalsPaid",
          header: "Renewals paid",
          meta: { fit: true },
          cell: ({ row }) => {
            const r = row.original.renewals;
            if (!r || r.renewalsSeen === 0) return <span className="text-muted-foreground">None yet</span>;
            return (
              <span className="flex flex-col">
                <span className="tabular-nums">
                  {formatNumber(r.renewalsPaid)} of {formatNumber(r.renewalsSeen)}
                </span>
                {r.atCap > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {formatNumber(r.atCap)} at cap
                  </span>
                )}
              </span>
            );
          },
        },
      ] satisfies ColumnDef<ProgramAnalyticsRow>[])
    : []),
  {
    accessorKey: "status",
    header: "Status",
    meta: { fit: true },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
  {
    id: "open",
    header: "",
    meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
    cell: () => <IconChevronRight size={16} stroke={1.75} className="text-muted-foreground" aria-hidden />,
  },
];

function AnalyticsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
          <div className="h-4 w-16 rounded bg-muted" />
          <div className="h-6 w-40 rounded bg-muted" />
        </div>
        <MetricStripSkeleton count={4} columns="lg:grid-cols-4" />
      </div>
      <DashboardCard title="Programs" bodyClassName="px-3 pb-3">
        <DataTable columns={programColumns(false)} data={[]} isLoading skeletonRows={4} />
      </DashboardCard>
    </div>
  );
}

/**
 * Affiliate Programs > Analytics. One row per program with its period
 * figures; once Recharge is connected the renewal columns show how each
 * program's recurring commission is doing and how many subscriptions have
 * used up the paid-renewal cap.
 */
export function ProgramsAnalytics() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ["programs-analytics", days],
    queryFn: () => programService.getProgramsAnalytics(days),
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const refreshing = query.isFetching && !!data;

  const metrics = useMemo<Metric[]>(() => {
    if (!data) return [];
    const { period, previous } = data.totals;
    const base: Metric[] = [
      {
        id: "programs-revenue",
        label: "Revenue",
        value: formatAmount(period.revenue),
        change: describeChange(period.revenue, previous.revenue, days, formatAmount),
      },
      {
        id: "programs-orders",
        label: "Orders",
        value: formatNumber(period.orders),
        change: describeChange(period.orders, previous.orders, days, formatNumber),
      },
      {
        id: "programs-commission",
        label: "Commission",
        value: formatAmount(period.commission),
        change: describeChange(period.commission, previous.commission, days, formatAmount),
      },
      {
        id: "programs-sellers",
        label: "Selling affiliates",
        value: formatNumber(period.sellers),
        change: `${formatNumber(data.totals.affiliates)} enrolled across ${formatNumber(data.totals.programs)} ${data.totals.programs === 1 ? "program" : "programs"}`,
      },
    ];
    if (!data.recurring) return base;
    const r = data.recurring;
    return [
      ...base,
      {
        id: "programs-recurring-commission",
        label: "Renewal commission",
        value: formatAmount(r.period.commission),
        change: describeChange(r.period.commission, r.previous.commission, days, formatAmount),
      },
      {
        id: "programs-active-subscriptions",
        label: "Active subscriptions",
        value: formatNumber(r.active),
        change:
          r.subscriptions === 0
            ? "No attributed subscriptions yet"
            : r.atCap > 0
              ? `${formatNumber(r.atCap)} have used their paid renewals`
              : "None at the paid-renewal cap",
      },
    ];
  }, [data, days]);

  if (query.isLoading || !data) {
    if (query.isError) {
      return (
        <div className="space-y-4">
          <PageHeader title="Program analytics" />
          <EmptyState
            title="Could not load program analytics"
            body="Check your connection and try again"
            action={
              <Button variant="outline" onClick={() => query.refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <PageHeader title="Program analytics" />
        <AnalyticsSkeleton />
      </div>
    );
  }

  const withRecurring = data.recurring !== null;

  return (
    <div className="space-y-4">
      <PageHeader title="Program analytics" />

      <MetricPanel
        id="programs-activity-panel"
        title="Activity"
        metrics={metrics}
        columns={withRecurring ? "lg:grid-cols-6" : "lg:grid-cols-4"}
        loading={refreshing}
        action={<PeriodToggle value={days} onChange={setDays} disabled={refreshing} />}
      >
        {data.recurring && !data.recurring.connected && (
          <p className="px-5 pb-4 text-xs text-muted-foreground">
            Recharge is disconnected. Renewal figures stop at the disconnect.{" "}
            <Link to="/brand/integrations/recharge" className="font-medium text-foreground hover:underline">
              Reconnect
            </Link>
          </p>
        )}
      </MetricPanel>

      <DashboardCard title="Programs" to="/brand/programs" bodyClassName="px-3 pb-3">
        <DataTable
          columns={programColumns(withRecurring)}
          minTableWidth={withRecurring ? "min-w-[64rem]" : "min-w-[40rem]"}
          data={data.programs}
          isLoading={refreshing}
          skeletonRows={4}
          getRowId={(row) => row.programId}
          onRowClick={(row) => navigate(`/brand/programs/${row.programId}`)}
          emptyText="No programs yet"
        />
      </DashboardCard>

      {!withRecurring && (
        <DashboardCard title="Renewals">
          <EmptyState
            bordered={false}
            icon={<IconPlug stroke={1.75} />}
            title="Connect Recharge to track renewal commissions"
            body="Subscriptions per program, renewals paid against the cap, and churn"
            action={
              <Button asChild variant="outline">
                <Link to="/brand/integrations/recharge">Connect Recharge</Link>
              </Button>
            }
          />
        </DashboardCard>
      )}
    </div>
  );
}

export default ProgramsAnalytics;
